import React from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';

import {
  GetResourceValue,
  GetZonesByIds,
  UpdateGameDocState
} from '../../../utils/game-document';
import PopupMenu from '../../popupMenu';
import { MapConfigStore } from '../../../pages/game-designer';
import { ZoneEntity } from '../../../types/game-document/entities';
import { GameDocumentContext } from '../../../contexts/game-document';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

export const zoneAreaMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

export const Zones: React.FC<{
  searchContentText: string;
  handleZoneMenuSelected: (id: string, menu: MenuList) => void;
}> = ({ searchContentText, handleZoneMenuSelected }) => {
  const { gameId } = useParams();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);
  const { gameDocument } = state;

  const zones = React.useMemo(() => {
    if (!state.gameDocument) return [];

    const zoneIds = state.gameDocument?.rules?.worldMap?.zones?.map(
      (zone) => zone.zoneAssId
    );

    const zones = GetZonesByIds(state.gameDocument, zoneIds);
    return zones;
  }, [state.gameDocument]);

  const filteredZones = React.useMemo(() => {
    return zones.filter((zone) => {
      const zoneTitle = GetResourceValue(gameDocument!, zone.titleResId ?? '');
      return zoneTitle.toLowerCase().includes(searchContentText.toLowerCase());
    });
  }, [zones, gameDocument, searchContentText]);

  const showZone = (zone: ZoneEntity, isVisible: boolean) => {
    if (gameDocument) {
      const newGame = { ...gameDocument };
      const { zones } = gameDocument.assets;

      if (zones) {
        const zoneIndex = zones.findIndex((x) => x.id === zone.id);
        zones[zoneIndex].isVisible = isVisible;
        newGame.assets.zones = zones;
        setState((state) => UpdateGameDocState(state, newGame));
      }
    }
  };

  if (
    filteredZones.length === 0 ||
    (searchContentText && filteredZones.length === 0)
  )
    return null;

  return (
    <PanelBar
      onSelect={() => {
        setMapConfig((prev) => ({
          ...prev,
          mapContentZones: !prev.mapContentZones
        }));
      }}>
      <PanelBarItem
        id={'zone-panel-bar'}
        expanded={mapConfig.mapContentZones}
        className={'panel-bar-custom'}
        title={
          <div className={'d-flex gap-2 align-items-center'}>
            <span
              className="material-symbols-outlined"
              style={{
                color: '#14BF8B'
              }}>
              center_focus_strong
            </span>
            Zones
          </div>
        }>
        {filteredZones.map((zone, index) => (
          <div
            key={zone.id}
            className={
              'd-flex w-100 justify-content-between align-items-center mt-1 gap-2'
            }>
            <div
              id={`zoneList-${zone.boundary.id}`}
              className={'card w-100 border-0'}
              style={{
                backgroundColor: '#14BF8B',
                borderColor: '#e5633d',
                borderWidth: '3px'
              }}>
              <div
                className={
                  'card-body p-2 d-flex w-100 justify-content-between align-items-center gap-2'
                }>
                <div
                  className={'d-flex gap-2 text-light align-items-center'}
                  style={{
                    minWidth: 0,
                    flex: 1
                  }}>
                  <span className={'material-symbols-outlined flex-shrink-0'}>
                    center_focus_strong
                  </span>
                  <span
                    title={GetResourceValue(
                      state.gameDocument!,
                      zone.titleResId ?? ''
                    )}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0
                    }}>
                    {GetResourceValue(
                      state.gameDocument!,
                      zone.titleResId ?? ''
                    )}
                  </span>
                </div>
                <div className={'flex-shrink-0'}>
                  <PopupMenu
                    id={index}
                    menus={zoneAreaMenu}
                    onMenuSelected={(_, mn) =>
                      handleZoneMenuSelected(zone.id, mn)
                    }
                    buttonColor="white"
                    iconClass={'k-icon k-i-more-vertical h5 mb-0 text-light'}
                  />
                </div>
              </div>
            </div>

            <span
              onClick={() => showZone(zone, !zone.isVisible)}
              className={`${zone.isVisible ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
              {zone.isVisible ? 'visibility' : 'visibility_off'}
            </span>
          </div>
        ))}
      </PanelBarItem>
    </PanelBar>
  );
};
